// Paste-to-create-spools modal. Workshop reads off a 2-column table
// from Excel / a chat message / wherever — first column is the spool
// number, second is the description. Tab, comma, or 2+ spaces split
// the columns; blank lines are skipped. Live preview shows what's
// about to be inserted before the user commits, so a sloppy paste
// can be corrected without leaving the modal.
import { useMemo, useState } from "react";
import { newDraft, type SpoolDraft } from "../components/spoolDraft";

interface Props {
  open: boolean;
  startSortOrder: number;
  existingNums: string[];
  onClose(): void;
  onCommit(rows: SpoolDraft[]): void;
}

interface ParsedRow {
  num: string;
  description: string;
  warning: string | null;
}

function parsePaste(text: string, existing: Set<string>): ParsedRow[] {
  const rows: ParsedRow[] = [];
  const seenInPaste = new Set<string>();
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    // Split on tab > comma > 2+ spaces. Description is everything
    // after the first separator so notes with commas survive intact.
    let num = "";
    let description = "";
    const tabIdx = line.indexOf("\t");
    if (tabIdx >= 0) {
      num = line.slice(0, tabIdx).trim();
      description = line.slice(tabIdx + 1).trim();
    } else {
      const commaIdx = line.indexOf(",");
      if (commaIdx >= 0) {
        num = line.slice(0, commaIdx).trim();
        description = line.slice(commaIdx + 1).trim();
      } else {
        const m = /^(\S+)\s{2,}(.*)$/u.exec(line);
        if (m) {
          num = m[1] ?? "";
          description = (m[2] ?? "").trim();
        } else {
          num = line;
          description = "";
        }
      }
    }
    if (!num) continue;
    let warning: string | null = null;
    if (num.length > 64) warning = "Spool number too long (max 64 chars)";
    else if (existing.has(num.toLowerCase())) warning = "Already exists in this job";
    else if (seenInPaste.has(num.toLowerCase())) warning = "Duplicate row in paste";
    seenInPaste.add(num.toLowerCase());
    rows.push({ num, description, warning });
  }
  return rows;
}

export function PasteSpoolsModal({
  open,
  startSortOrder,
  existingNums,
  onClose,
  onCommit,
}: Props) {
  const [text, setText] = useState("");

  const existing = useMemo(
    () => new Set(existingNums.map((n) => n.toLowerCase())),
    [existingNums],
  );
  const parsed = useMemo(() => parsePaste(text, existing), [text, existing]);
  const okRows = parsed.filter((r) => r.warning === null);

  if (!open) return null;

  const commit = () => {
    if (okRows.length === 0) return;
    const drafts: SpoolDraft[] = okRows.map((r, i) => {
      const draft = newDraft(startSortOrder + i);
      return { ...draft, num: r.num, description: r.description || null };
    });
    onCommit(drafts);
    setText("");
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
      }}
    >
      <div className="modal" style={{ width: "min(680px, 90vw)", maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>Paste spools</span>
          <button className="btn-sm" onClick={onClose} aria-label="Close" style={{ marginLeft: "auto" }}>×</button>
        </div>
        <div className="modal-body" style={{ overflow: "auto", paddingBottom: 4 }}>
          <p style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginTop: 0, lineHeight: 1.5 }}>
            Paste a 2-column table — spool number then description. Tab, comma,
            or two-or-more spaces separate the columns; blank lines are
            ignored. A single column (just spool numbers) works too.
          </p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={"SP-001\tBay 1 framework\nSP-002\tBay 2 framework\nSP-003, Spare"}
            style={{
              width: "100%",
              minHeight: 140,
              fontFamily: "var(--bsp-font-mono)",
              fontSize: 12,
              padding: 8,
              border: "1px solid var(--bsp-border-strong)",
              borderRadius: 4,
              resize: "vertical",
            }}
          />
          {parsed.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{
                fontFamily: "var(--bsp-font-display)",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "var(--bsp-blue)",
                marginBottom: 6,
              }}>
                Preview ({okRows.length} ready, {parsed.length - okRows.length} skipped)
              </div>
              <div style={{ border: "1px solid var(--bsp-border)", borderRadius: 4, maxHeight: 240, overflow: "auto" }}>
                <table className="stages-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "30%" }}>Num</th>
                      <th>Description</th>
                      <th style={{ width: "30%" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.map((r, i) => (
                      <tr key={i} className={r.warning ? "is-skipped" : ""}>
                        <td style={{ fontFamily: "var(--bsp-font-mono)", fontWeight: 700, color: "var(--bsp-blue)" }}>
                          {r.num}
                        </td>
                        <td style={{ fontSize: 12, color: "var(--bsp-dark)" }}>
                          {r.description || <span style={{ color: "var(--bsp-fg-muted)", fontStyle: "italic" }}>—</span>}
                        </td>
                        <td style={{ fontSize: 11, color: r.warning ? "var(--bsp-red-fg)" : "var(--bsp-green-fg, #166534)" }}>
                          {r.warning ?? "Will be added"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-sm" onClick={onClose}>Cancel</button>
          <button
            className="btn-submit"
            onClick={commit}
            disabled={okRows.length === 0}
          >
            Add {okRows.length === 0 ? "spools" : okRows.length === 1 ? "1 spool" : `${okRows.length} spools`}
          </button>
        </div>
      </div>
    </div>
  );
}
